import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl-next';

import Button, { IconButton } from '@atlaskit/button/new';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/core/migration/show-more-horizontal--more';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { ChatPillIcon } from '@atlaskit/rovo-agent-components';
import { AgentDropdownMenu } from '@atlassian/conversation-assistant-agent';
import { type AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service';

import { messages } from './messages';

type AgentActionsProps = {
	agentId: string;
	isAgentCreatedByUser: boolean;
	onEditAgent: () => void;
	onCopyAgent: () => void;
	onDuplicateAgent: () => void;
	onDeleteAgent: () => void;
	onChatClick: () => void;
	isForgeAgent: boolean;
} & AssistanceServiceInputParam;

interface ActionMenuItem {
	text: React.ReactNode;

	onClick?: () => void;

	isDisabled?: boolean;
}

const chatToAgentButtonContainer = xcss({
	width: '100%',
});

const chatToAgentButtonWrapper = xcss({
	display: 'flex',
	justifyContent: 'center',
	lineHeight: '20px',
	fontWeight: 'font.weight.medium',
});

const chatPillTextStyles = xcss({
	wordBreak: 'break-word',
	textAlign: 'left',
	whiteSpace: 'pre-wrap',
});

const chatPillIconWrapper = xcss({
	minWidth: '20px',
	height: '20px',
});

const buildAgentActions = ({
	onDuplicateAgent,
	onCopyAgent,
}: {
	onCopyAgent: () => void;
	onDuplicateAgent: () => void;
}): ActionMenuItem[] => {
	return [
		{
			text: <FormattedMessage {...messages.actionDuplicate} />,
			onClick: onDuplicateAgent,
		},
		{
			text: <FormattedMessage {...messages.actionCopyLink} />,
			onClick: onCopyAgent,
		},
	];
};
const buildAgentSettings = ({
	onEditAgent,
	onDeleteAgent,
}: {
	onEditAgent: () => void;
	onDeleteAgent: () => void;
}): ActionMenuItem[] => {
	return [
		{
			text: <FormattedMessage {...messages.actionEdit} />,
			onClick: onEditAgent,
		},
		{
			text: <FormattedMessage {...messages.actionDelete} />,
			onClick: onDeleteAgent,
		},
	];
};

export const AgentActions = ({
	onEditAgent,
	onDeleteAgent,
	onDuplicateAgent,
	onCopyAgent,
	onChatClick,
	isAgentCreatedByUser,
	agentId,
	isForgeAgent,
	assistanceServiceParams,
}: AgentActionsProps) => {
	const { formatMessage } = useIntl();

	const agentActions = buildAgentActions({ onDuplicateAgent, onCopyAgent });
	const agentSetting = buildAgentSettings({ onEditAgent, onDeleteAgent });

	return (
		<Inline space="space.100">
			<Box xcss={chatToAgentButtonContainer}>
				<Button shouldFitContainer onClick={onChatClick}>
					<Box xcss={chatToAgentButtonWrapper}>
						<Inline space="space.050">
							<Box xcss={chatPillIconWrapper}>
								<ChatPillIcon />
							</Box>
							<Box xcss={chatPillTextStyles}>{formatMessage(messages.actionChatToAgent)}</Box>
						</Inline>
					</Box>
				</Button>
			</Box>

			{fg('rovo_use_agent_permissions') ? (
				<AgentDropdownMenu
					agentId={agentId}
					isAgentCreatedByUser={isAgentCreatedByUser}
					isForgeAgent={isForgeAgent}
					onCopyAgent={onCopyAgent}
					onDeleteAgent={onDeleteAgent}
					onDuplicateAgent={onDuplicateAgent}
					onEditAgent={onEditAgent}
					loadPermissionsOnMount
					assistanceServiceParams={assistanceServiceParams}
				/>
			) : (
				<DropdownMenu<HTMLButtonElement>
					trigger={({ triggerRef, ...props }) => (
						<IconButton {...props} icon={MoreIcon} label="more" ref={triggerRef} />
					)}
					placement="bottom-end"
				>
					<DropdownItemGroup>
						{agentActions.map(({ text, onClick }, idx) => {
							return (
								<DropdownItem key={idx} onClick={onClick}>
									{text}
								</DropdownItem>
							);
						})}
					</DropdownItemGroup>
					{isAgentCreatedByUser && (
						<DropdownItemGroup hasSeparator>
							{agentSetting.map(({ text, onClick }, idx) => {
								return (
									<DropdownItem key={idx} onClick={onClick}>
										{text}
									</DropdownItem>
								);
							})}
						</DropdownItemGroup>
					)}
				</DropdownMenu>
			)}
		</Inline>
	);
};
